import type * as Y from 'yjs'
import type { Asset } from '@/types'
import type { CopyPlacements } from '@/lib/lineageApi'

/**
 * The lineage fields of a record copied in the browser (a paste, a Cmd+D, an
 * Option-drag). The source's markers name the source's own placement and the
 * answers given about that node, so neither rides along on the clone — the
 * backend strips the same fields from the copies it makes (`stripAssetLineage`).
 * The clone carries its own event when the ledger placed it before the write
 * (`placementsForCopies`); otherwise it lands unmarked and the guard counts it.
 */
export function copiedLineageFields(
  nodeId: string,
  placements: CopyPlacements | null,
): Pick<Asset, 'lineageEvent' | 'lineageDismissed'> {
  return { lineageEvent: placements?.get(nodeId), lineageDismissed: undefined }
}

/**
 * The lineage markers of a copied document (ADR-0021). A copied record still
 * carries the SOURCE node's `lineageEvent`, which names a placement in the
 * source project, not in the copy. The backend placed the copy's nodes before
 * answering (`DeepCopyIdMaps.lineage`): each placed node gets the copy's event,
 * and every other node loses the source's marker rather than carrying one that
 * is not its own. With the ledger off (`lineage` absent) every marker is
 * dropped — the copy starts without a story the guard could check.
 */
export function applyCopyLineageMarkers(
  yAssets: Y.Map<Asset>,
  lineage: { event: string; placedNodeIds: readonly string[] } | undefined,
): { stamped: number; stripped: number } {
  const placed = new Set(lineage?.placedNodeIds ?? [])
  let stamped = 0
  let stripped = 0
  for (const [id, asset] of Array.from(yAssets.entries())) {
    if (lineage && placed.has(id)) {
      yAssets.set(id, { ...asset, lineageEvent: lineage.event })
      stamped += 1
    } else if (asset.lineageEvent !== undefined) {
      const { lineageEvent: _dropped, ...rest } = asset
      yAssets.set(id, rest as Asset)
      stripped += 1
    }
  }
  return { stamped, stripped }
}
